import { Component, EventEmitter, OnInit, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DateRange } from '../../../../shared/components/date-range.component';
import { forkJoin } from 'rxjs';
import { Vessel } from '../../vessels.service';
import { MessageLevel, PgMessagesService } from '../../../../shared/services/pg-messages.service';
import moment from 'moment';
import { DateTimeHelpers } from '../../../../shared/helpers/date-time.helpers';
import {
  PgDateRangeTitleComponent,
  PgDateRangeTitleEnum,
  PgTitleComponent
} from '../../../../shared/components/title-bar/title-bar.component';
import {
  NoonReportsV202205Service,
  VesselReportV202205
} from '../../services/noon-reports-v202205.service';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { saveAs } from 'file-saver';
import { VesselReportToolbarComponent } from '../../vessel-report-toolbar/vessel-report-toolbar.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { AgGridModule } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { AgGridService } from 'src/app/shared/ag-grid/ag-grid.service';
import { GeoCoordinateDMS } from 'src/app/transportation/models/GeoCoordinate';
import { UserLocalStoreService } from 'src/app/shared/services/user-local-store.service';

@Component({
  selector: 'app-noon-reports-v202205',
  templateUrl: './noon-reports-v202205.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, AgGridModule]
})
export class NoonReportsV202205Component implements OnInit, PgTitleComponent, PgDateRangeTitleComponent {

  title = new EventEmitter<string>();
  dateRangeSubTitle = new EventEmitter<[DateRange, PgDateRangeTitleEnum]>();

  toolbarComponentType = VesselReportToolbarComponent;
  toolbarComponent!: VesselReportToolbarComponent;

  gridOptions: GridOptions = {};
  gridRestored: boolean = false;

  dateRange: DateRange;
  reports: VesselReportV202205[] = [];
  vessels: Vessel[] = [];
  organisationsMap!: Map<string, Organisation>;
  organisationsNameMap!: Map<string, string>;

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _noonReportsService: NoonReportsV202205Service,
    private _pgMessagesService: PgMessagesService,
    private _organisationsService: OrganisationService,
    private _renderer: Renderer2,
    private _userLocalStorage: UserLocalStoreService,
  ) {
    this.dateRange = {
      startDate: moment().startOf('day').subtract(7, 'd').toDate(),
      endDate: moment().endOf('day').toDate()
    };
  }

  columnDefs = [
    {
      headerName: 'Actions',
      cellRenderer: (params:ICellRendererParams) => {
        const container = this._renderer.createElement('div');
        const downloadBtn = AgGridService.downloadButton(
          this._renderer,
          () => this._noonReportsService.generateFile(params.data, this.organisationsNameMap),
        );
        this._renderer.appendChild(container, downloadBtn);
        return container;
      }
    },
    { headerName: 'Vessel', field: 'vesselInfo.vesselName' },
    {headerName: 'Date', valueGetter: (params: ICellRendererParams) => params.data.reportDate?.isoDate?.isoDate ? DateTimeHelpers.formatDateString(params.data.reportDate.isoDate.isoDate)
      : '', sort: 'desc'},
    { headerName: 'Voyage Number', field: 'voyageNumber' },
    { headerName: 'Type', field: 'reportType' },
    {
      headerName: 'Lat/Long',
      cellRenderer: (params:ICellRendererParams) => this.transform(params.data.vesselLocation.geoCoordinate)
    },
    {
      headerName: 'From',
      valueGetter: (params:ICellRendererParams) => {
        const from = this.organisationsMap.get(params.data.currentPlan.fromOrganisationId);
        return (from) ? from.nameTranslations['en'] : '';
      }
    },
    {
      headerName: 'To',
      valueGetter: (params:ICellRendererParams) => {
        const to = this.organisationsMap.get(params.data.currentPlan.toOrganisationId);
        return (to) ? to.nameTranslations['en'] : '';
      }
    },
    { headerName: 'ETA', field: 'currentPlan.eta' },
    { headerName: 'Speed', field: 'speed.averageCurrentReportSpeed' },
    { headerName: 'Speed', field: 'speed.averageVoyageSpeed' },
    { headerName: 'Distance to go', field: 'distances.distanceCovered' },
  ];

  transform(value: any): any {
    if (value) {
      const val = value as GeoCoordinateDMS;
      return `${val?.latitude?.degree}°${val?.latitude?.minute}'${val?.latitude?.direction?.toUpperCase()} ` +
        `${val?.longitude?.degree}°${val?.longitude?.minute}'${val?.longitude?.direction?.toUpperCase()}`;
    }
    return null;
  }

  ngOnInit(): void {
    this.gridOptions = AgGridService.getOptions();

    this.title.emit('Noon reports V202205');
    this._activatedRoute.queryParams.subscribe(p => {
      const startDate = +p['startTime'];
      const endDate = +p['endTime'];
      this.dateRange = {
        startDate: new Date(startDate),
        endDate: new Date(endDate)
      };
      this.dateRangeSubTitle.emit([this.dateRange, PgDateRangeTitleEnum.defaultTitle]);
      console.log('We are coming here to get the reports?');
      this.getReport();
    });

    this.getParameters();
    this.toolbarComponent.date.subscribe(dateRange => {
      this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this._router
          .navigate(['/vessels/noon-reports/v202205'], {
            queryParams: {
              startTime: dateRange?.startDate.getTime(),
              endTime: dateRange?.endDate.getTime()
            }
          })
          .then((e) => console.log(`Result of Navigation is ${e}`));
      });
    });
  }

  gridSetting(event: GridReadyEvent) {
    AgGridService.setting(event);
  }

  saveGridState(event: GridReadyEvent) {
    AgGridService.saveGridState(event, this.gridRestored, this._userLocalStorage, 'noonReportGridV2');
  }

  restoreGridState(event: GridReadyEvent) {
    this.gridRestored = AgGridService.restoreGridState(event, this._userLocalStorage, 'noonReportGridV2');
  }

  getParameters() {
    this._activatedRoute.queryParams.subscribe(p => {
      const startDate = +p['startTime'];
      const endDate = +p['endTime'];
      this.dateRange = {
        startDate: new Date(startDate),
        endDate: new Date(endDate)
      };
      this.toolbarComponent.dateRange = this.dateRange;
      this.title.emit('Noon Reports');
      this.dateRangeSubTitle.emit([this.dateRange, PgDateRangeTitleEnum.defaultTitle]);
    });
  }

  getReport() {
    console.log('We are coming here to get the reports? I mean here.');
    forkJoin([
      this._noonReportsService.getNoonReportsV202205Between(this.dateRange),
      this._organisationsService.getOrganisationIdToOrganisationMap(),
      this._organisationsService.getOrganisationIdToOrganisationNameMap()
    ]).subscribe(results => {
      this.reports = results[0].sort((r1, r2) => moment(r2.reportDate?.isoDate?.isoDate).diff(r1.reportDate?.isoDate?.isoDate));
      this.organisationsMap = results[1];
      this.organisationsNameMap = results[2];
    });
  }

  formatDate(timestamp: number) {
    if (timestamp > 0) {
      return DateTimeHelpers.formatLocalDateTime(timestamp);
    } else {
      return '';
    }
  }

  downloadReport() {
    this._noonReportsService.downloadReports(this.dateRange.startDate.getTime(),
      this.dateRange.endDate.getTime()).subscribe(b => {
      saveAs(b.body!, `${DateTimeHelpers.formatLocalDate(this.dateRange.startDate.getTime())}-to-` +
          `${DateTimeHelpers.formatLocalDate(this.dateRange.endDate.getTime())}-reports.xlsx`);
    }, () => {
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Error,
        message: 'Could not download noon reports',
        topic: 'Voyages report'
      });
    });
  }
}
